import React, { useEffect } from "react";
import { ArrowRight, AlertTriangle, CheckCircle2, TrendingUp, Phone, Target, Calendar, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/hooks/useSettings";
import { useNavigate } from "react-router-dom";
import DiagnosticRDV from "@/components/diagnostic/DiagnosticRDV";

const DIM_LABELS = {
  acquisition: "Acquisition",
  previsibilite: "Prévisibilité",
  qualification: "Qualification",
  reactivite: "Réactivité",
  transformation: "Transformation",
  suivi_devis: "Suivi devis",
  digitalisation: "Digitalisation",
};

const LEVEL_CONFIG = {
  solide: { color: "text-green-600", bg: "bg-green-50", border: "border-green-200", label: "Solide" },
  "améliorable": { color: "text-yellow-600", bg: "bg-yellow-50", border: "border-yellow-200", label: "Améliorable" },
  fragile: { color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200", label: "Fragile" },
  critique: { color: "text-red-600", bg: "bg-red-50", border: "border-red-200", label: "Critique" },
};

export default function DiagnosticResults({ result, answers, contact }) {
  const { settings } = useSettings();
  const navigate = useNavigate();
  const logoUrl = settings?.logo_light || "/logo_transparent.png";

  const { scores, calculations: calcs, priorities, level, recommended_solutions } = result;
  const benchmark = result.benchmark || null;
  const lvl = LEVEL_CONFIG[level] || LEVEL_CONFIG["améliorable"];

  const dims = Object.entries(DIM_LABELS).map(([key, label]) => ({
    key, label, score: scores[key] || 0,
  }));

  // Fire retargeting events per problem detected
  useEffect(() => {
    const track = (name, params = {}) => {
      if (typeof window.fbq === "function") window.fbq("trackCustom", name, params);
      if (typeof window.gtag === "function") window.gtag("event", name, params);
    };
    track("DiagnosticResult", { score: scores.overall, level });
    if (priorities) {
      priorities.forEach((p) => {
        const dim = p.dimension || p.id;
        if (dim) track(`Problem_${dim}`, { severity: p.severity, score: scores[dim] });
      });
    }
  }, [scores, level, priorities]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 text-white py-10 px-4">
        <div className="max-w-3xl mx-auto text-center">
          {logoUrl && <img src={logoUrl} alt="Lead Artisan" className="h-10 mx-auto mb-6 opacity-80" />}
          <p className="section-label text-accent mb-3">Votre diagnostic Lead Artisan</p>
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-md ${lvl.bg} ${lvl.border} border-2 mb-6`}>
            <span className={`text-4xl sm:text-5xl font-black ${lvl.color}`}>{scores.overall}</span>
            <span className={`text-lg font-bold ${lvl.color}`}>/100</span>
          </div>
          <p className="text-slate-400 text-sm">
            Niveau : <strong className={lvl.color}>{lvl.label}</strong>
          </p>
          {contact?.first_name && (
            <p className="text-slate-500 text-xs mt-2">{contact.first_name} {contact.last_name} — {contact.company}</p>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">

        {/* ─── 7 Dimensions ─── */}
        <div className="bg-white border-2 border-slate-200 rounded-md p-6" data-testid="scores-section" style={{ boxShadow: "4px 4px 0px rgba(30,90,158,0.1)" }}>
          <h3 className="text-lg font-bold text-slate-900 mb-6">Vos 7 dimensions</h3>
          <div className="space-y-4">
            {dims.map((d) => {
              const barColor = d.score >= 80 ? "bg-green-500" : d.score >= 60 ? "bg-yellow-500" : d.score >= 40 ? "bg-orange-500" : "bg-red-500";
              return (
                <div key={d.key}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-slate-700">{d.label}</span>
                    <span className="text-sm font-bold text-slate-900">{d.score}/100</span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full transition-all duration-700 ${barColor}`} style={{ width: `${d.score}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ─── Situation / Objectif / Écart ─── */}
        <div className="grid sm:grid-cols-3 gap-4">
          <StatBlock label="Situation actuelle" value={`${calcs.clients_actuels} clients/mois`} sub="Actuellement" />
          <StatBlock label="Votre objectif" value={`${calcs.clients_objectif} clients/mois`} sub="Souhaité" accent />
          <StatBlock label="L'écart" value={`+${calcs.clients_manquants} clients`} sub="À trouver" highlight />
        </div>

        {/* ─── Potentiel financier ─── */}
        {calcs.ca_potentiel_supplementaire > 0 && (
          <div className="bg-accent/5 border-2 border-accent/30 rounded-md p-6 text-center" data-testid="ca-potentiel">
            <p className="text-sm text-slate-600 mb-2">Ce que cet écart peut représenter</p>
            <p className="text-3xl sm:text-4xl font-black text-accent">
              {calcs.ca_potentiel_supplementaire.toLocaleString("fr-CH")} CHF
            </p>
            <p className="text-sm text-slate-500">de chiffre d'affaires supplémentaire potentiel / mois</p>
            {calcs.taux_transformation > 0 && calcs.devis_necessaires > 0 && (
              <p className="text-xs text-slate-400 mt-3">
                Avec votre taux de transformation de {calcs.taux_transformation}%, cela nécessiterait environ {calcs.devis_necessaires} devis/opportunités supplémentaires.
              </p>
            )}
            <p className="text-xs text-slate-400 mt-2 italic">
              Estimation basée sur vos réponses. Il ne s'agit pas d'une garantie de résultat.
            </p>
          </div>
        )}

        {/* ─── Benchmark Métier ─── */}
        {benchmark && (
          <div className="bg-green-50 border-2 border-green-200 rounded-md p-5" data-testid="benchmark-section">
            <h3 className="text-sm font-bold text-green-800 mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Référence métier : {answers?.metier} — {benchmark.canton || "Tous cantons"}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {benchmark.cpl_moyen && (
                <div className="text-center">
                  <p className="text-lg font-black text-green-700">{benchmark.cpl_moyen} CHF</p>
                  <p className="text-xs text-green-600">Coût / lead moyen</p>
                </div>
              )}
              {benchmark.taux_qualification && (
                <div className="text-center">
                  <p className="text-lg font-black text-green-700">{benchmark.taux_qualification}%</p>
                  <p className="text-xs text-green-600">Taux qualification</p>
                </div>
              )}
              {benchmark.taux_rdv && (
                <div className="text-center">
                  <p className="text-lg font-black text-green-700">{benchmark.taux_rdv}%</p>
                  <p className="text-xs text-green-600">Taux RDV</p>
                </div>
              )}
              {benchmark.taux_signature && (
                <div className="text-center">
                  <p className="text-lg font-black text-green-700">{benchmark.taux_signature}%</p>
                  <p className="text-xs text-green-600">Taux signature</p>
                </div>
              )}
            </div>
            <p className="text-xs text-green-600 mt-3 italic">Données basées sur notre référentiel. Résultats variables selon votre marché local.</p>
          </div>
        )}

        {/* ─── 3 Priorités ─── */}
        {priorities.length > 0 && (
          <div data-testid="priorities-section">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-accent" />
              Vos {priorities.length} priorités
            </h3>
            <div className="space-y-3">
              {priorities.map((p, i) => {
                const sev = p.severity === "critical" ? "border-red-300 bg-red-50" : p.severity === "fragile" ? "border-orange-300 bg-orange-50" : "border-yellow-300 bg-yellow-50";
                return (
                  <div key={p.id} className={`border-2 rounded-md p-5 ${sev}`}>
                    <div className="flex items-start gap-3">
                      <span className="text-lg font-black text-slate-400">#{i + 1}</span>
                      <div>
                        <h4 className="font-bold text-slate-900">{p.title}</h4>
                        <p className="text-sm text-slate-600 mt-1">{p.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ─── Solutions recommandées ─── */}
        {recommended_solutions.length > 0 && (
          <div data-testid="solutions-section">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              Solutions recommandées
            </h3>
            <div className="space-y-3">
              {recommended_solutions.map((s) => (
                <div key={s.dimension} className="bg-white border-2 border-slate-200 rounded-md p-5" style={{ boxShadow: "3px 3px 0px rgba(30,90,158,0.08)" }}>
                  <h4 className="font-bold text-slate-900 mb-1">{s.title}</h4>
                  <p className="text-sm text-slate-600">{s.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─── CTA Final ─── */}
        <div className="bg-slate-900 rounded-md p-8 text-center" style={{ boxShadow: "6px 6px 0px rgba(249,115,22,0.3)" }}>
          <h3 className="text-xl sm:text-2xl font-black text-white mb-3">
            Vous savez maintenant où sont les fuites.
          </h3>
          <p className="text-slate-400 mb-6">
            Réservez un appel stratégie de 30 min avec un expert pour définir votre plan d'action.
          </p>
        </div>

        {/* ─── RDV Calendar ─── */}
        <DiagnosticRDV contact={contact} diagnosticId={result?.id} />

        {/* Back */}
        <div className="text-center pb-8">
          <button onClick={() => navigate("/")} className="text-sm text-slate-400 hover:text-primary transition-colors">
            ← Retour au site Lead Artisan
          </button>
        </div>
      </div>
    </div>
  );
}

function StatBlock({ label, value, sub, accent, highlight }) {
  return (
    <div className={`bg-white border-2 rounded-md p-5 text-center ${highlight ? "border-accent" : "border-slate-200"}`}
      style={{ boxShadow: "3px 3px 0px rgba(30,90,158,0.08)" }}>
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className={`text-xl font-black ${highlight ? "text-accent" : accent ? "text-primary" : "text-slate-900"}`}>{value}</p>
      <p className="text-xs text-slate-400">{sub}</p>
    </div>
  );
}
